import { inject, Injectable } from '@angular/core';
import { ArrayOfSavingsAccounts } from '@brightside-web/desktop/feature/savings-account';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import {
  FinancialAccountFundsSource,
  FinancialAccountsCoreService,
  FinancialAccountTypes,
} from '@brightside-web/desktop/feature/financial-accounts-core';
import {
  BsApiService,
  GenericResponseCode,
  GenericResponseResult,
  SimpleGlobalModalService,
} from '@brightside-web/desktop/data-access/core-services';
import { take } from 'rxjs/operators';
import { CurrencySelectAmountOption } from '@brightside/brightside-ui';
import { Router } from '@angular/router';

export interface autosavePayload {
  amount: number;
  schedule_type: string;
  source: string;
  next_transfer_date: string;
  paused_ts: string | null;
}

@Injectable()
export class AutosaveService {
  sub = new Subscription();

  apiName = 'api-mobile';

  defaultOptions: CurrencySelectAmountOption[] = [
    { value: 25, selected: false },
    { value: 50, selected: false },
    { value: 100, selected: false },
  ];

  defaultNotStartedOptions: CurrencySelectAmountOption[] = [
    { value: 25, selected: false },
    { value: 50, selected: false },
    { value: 100, selected: false },
  ];

  private _currentAutosaveInfo = new BehaviorSubject<FinancialAccountFundsSource | undefined>(undefined);
  public readonly currentAutosaveInfo: Observable<FinancialAccountFundsSource | undefined> =
    this._currentAutosaveInfo.asObservable();

  private _autoSaveAmountSelected = new BehaviorSubject<number | null>(null);
  public readonly autoSaveAmountSelected: Observable<number | null> = this._autoSaveAmountSelected.asObservable();

  private _autoSaveOnOff = new BehaviorSubject<boolean>(false);
  public readonly autoSaveOnOff: Observable<boolean> = this._autoSaveOnOff.asObservable();

  private _isOkToPost = new BehaviorSubject<boolean>(false);
  public readonly isOkToPost: Observable<boolean> = this._isOkToPost.asObservable();

  private _currencySelectOptions = new BehaviorSubject<CurrencySelectAmountOption[]>(this.defaultOptions);
  public readonly currentSelectionOption: Observable<CurrencySelectAmountOption[]> = this._currencySelectOptions.asObservable();

  private _fundSourceTruth: FinancialAccountFundsSource | undefined;

  private _processing = new BehaviorSubject<boolean>(false);
  public readonly processing: Observable<boolean> = this._processing.asObservable();
  routeOnSuccess: string[] = ['accounts', 'savings'];
  routeOnBack: string[];

  get fundSourceTruth() {
    return this._fundSourceTruth;
  }
  set fundSourceTruth(value: FinancialAccountFundsSource | undefined) {
    this._fundSourceTruth = value;
    if (value?.autoSaveSchedule.nextDepositAmount) {
      this._autoSaveAmountSelected.next(value?.autoSaveSchedule.nextDepositAmount);
    }
    if (value?.autoSaveSchedule.status === 'active') {
      this._autoSaveOnOff.next(true);
    } else if (value?.autoSaveSchedule.status === 'notStarted') {
      this._currencySelectOptions.next(this.defaultNotStartedOptions);
      if (!this._autoSaveAmountSelected.value) this._autoSaveAmountSelected.next(20);
    }
    if (value) this._currentAutosaveInfo.next(value);
  }

  private _faCoreServices: FinancialAccountsCoreService | undefined;

  get faCoreServices(): FinancialAccountsCoreService {
    if (!this._faCoreServices) {
      this._faCoreServices = inject(FinancialAccountsCoreService);
    }
    return this._faCoreServices;
  }

  constructor(private bsApi: BsApiService, private router: Router, private simpleModalSvc: SimpleGlobalModalService) {
    this.sub.add(
      combineLatest([this.autoSaveAmountSelected, this.autoSaveOnOff]).subscribe(([amount, autoSaveOnOff]) => {
        const status = this.fundSourceTruth?.autoSaveSchedule.status;
        const isAlreadyOn = status === 'active';
        const isAmountValid = amount !== null && amount > 0;
        const isAmountChanged = amount !== this.fundSourceTruth?.autoSaveSchedule.nextDepositAmount;
        const isStatusNotStarted = status === 'notStarted';

        const shouldPost =
          (isStatusNotStarted || isAlreadyOn !== autoSaveOnOff || (isAlreadyOn === autoSaveOnOff && isAmountChanged)) &&
          isAmountValid;

        this._isOkToPost.next(shouldPost);
      })
    );

    this.getAutoSavingsInfo();
  }

  getAutoSavingsInfo(force: boolean = false) {
    this._isOkToPost.next(false);
    this.getSavingsAccounts(force);
  }

  setAutoSaveAmount(amountToSave: number) {
    this._autoSaveAmountSelected.next(amountToSave);
  }

  getSavingsAccounts(force = false) {
    this.faCoreServices.getAccountsByType<ArrayOfSavingsAccounts>(FinancialAccountTypes.SAVINGS).subscribe((resp) => {
      if (resp.payload) {
        this.fundSourceTruth = resp.payload.accounts[0].fundsSource;
      } else {
        // we shouldn't be in autosave if there is an issue here... soooo something went wrong, try again modal
        // call this again, try a few times before exiting?
      }
    });
  }

  createAutoSaveSchedule() {
    if (this._isOkToPost.value) {
      const autosavePostObj: autosavePayload = {
        schedule_type: 'everyPaycheck',
        next_transfer_date: this._currentAutosaveInfo.value?.nextAvailableDate ?? '',
        amount: this._autoSaveAmountSelected.value ?? 0,
        source: 'payroll',
        paused_ts: null,
      };
      if (this.fundSourceTruth?.autoSaveSchedule.status === 'notStarted') {
        this.postAutoSavings(autosavePostObj);
      } else {
        if (!this._autoSaveOnOff.value && this.fundSourceTruth?.autoSaveSchedule.status === 'active') {
          this.showConfirmModal(autosavePostObj);
        } else {
          this.patchAutoSavings(autosavePostObj);
        }
      }
    }
  }

  private postAutoSavings(payload: autosavePayload) {
    this._processing.next(true);
    this.bsApi
      .post(this.apiName, '/accounts/savings/auto-schedule', payload)
      .pipe(take(1))
      .subscribe((resp) => {
        this.postPatchResponse(resp);
      });
  }

  private patchAutoSavings(payload: autosavePayload) {
    this._processing.next(true);
    this.bsApi
      .patch(this.apiName, '/accounts/savings/auto-schedule', payload)
      .pipe(take(1))
      .subscribe((resp) => {
        this.postPatchResponse(resp);
      });
  }

  postPatchResponse(resp: GenericResponseResult) {
    if (resp.result.code === GenericResponseCode.SERVICE_OK) {
      this.router.navigate(this.routeOnSuccess);
    } else {
      this.showRetryModal(resp);
      this._processing.next(false);
    }
  }

  showConfirmModal(autosavePostObj: autosavePayload) {
    const paused_ts = new Date().toISOString().split('T')[0];
    this.simpleModalSvc.createModal({
      title: 'AUTOSAVE_CONFIRM_DISABLE_TITLE',
      contentKey: 'AUTOSAVE_CONFIRM_DISABLE_DESC',
      displayCloseButton: false,
      eventName: 'autosave_confirm_disable',
      isError: false,
      size: 'medium',
      ctaKey: 'AUTOSAVE_CONFIRM_DISABLE_CTA1',
      ctaAction: () => {
        this.patchAutoSavings({ ...autosavePostObj, paused_ts });
      },
      secondaryCtaKey: 'AUTOSAVE_CONFIRM_DISABLE_CTA2',
      secondaryCtaAction: () => {
        this.simpleModalSvc.clearModal();
      },
    });
  }

  showRetryModal(resp: GenericResponseResult) {
    this.simpleModalSvc.createGenericErrorModal(`autosave_settings_${resp.result.code}`);
  }

  setAutoSaveFeature(enable: boolean) {
    this._autoSaveOnOff.next(enable);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  setDirty(b: boolean) {
    this._isOkToPost.next(b);
  }
}
